"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ops = exports.Executor = void 0;
const interfaces_1 = require("./interfaces");
const mem_reader_1 = require("./mem-reader");
const memory_1 = require("./memory");
const uint256_1 = require("./uint256");
const utils_1 = require("./utils");
const buffer_1 = require("buffer");
const keccak256_1 = __importDefault(require("keccak256"));
const compiler_1 = require("./compiler");
const ZERO = (0, uint256_1.U256)(0);
function asyncOp() {
    return function (target, propertyKey, descriptor) {
        target[propertyKey].isAsync = true;
    };
}
class Executor {
    constructor(state, code) {
        this.state = state;
        this.code = code;
        this.stack = [];
        this.mem = new memory_1.Memory();
        this.logs = [];
        this.stop = null;
        this._onResult = [];
        this.opSpy = null;
        this._onMemChange = null;
        this._onStartCall = null;
        this._onEndCall = null;
        this._onLog = null;
        this.notifyMemChanged = false;
        this.lastReturndata = new mem_reader_1.MemReader([]);
        this.knownSequence = null;
        this.run = code(this);
    }
    get contractName() {
        return this.code.contractName ?? (0, utils_1.dumpU256)(this.code.contractAddress);
    }
    get contractAbi() {
        return this.code.contractAbi;
    }
    get contractAddress() {
        return this.code.contractAddress;
    }
    async execute() {
        this.stop = null;
        const p = this.run();
        await p;
        const result = this.stop ?? {
            type: 'end of code',
            newState: this.state,
            gas: this.state.gasSpent,
        };
        for (const or of this._onResult) {
            or(result);
        }
        return result;
    }
    dumpStack() {
        return this.stack.map(i => (0, utils_1.dumpU256)(i)).reverse();
    }
    dumpCalldata() {
        const dumped = [];
        let offset = 0;
        const cd = this.state.calldata;
        if (cd.size % 32 === 4) {
            dumped.push(buffer_1.Buffer.from(cd.sliceNoPad(0, 4)).toString('hex'));
            offset = 4;
        }
        while (true) {
            const uint = cd.sliceNoPad(offset, 32);
            if (!uint.length) {
                return dumped;
            }
            dumped.push(buffer_1.Buffer.from(uint).toString('hex'));
            offset += 32;
        }
    }
    dumpMemory() {
        const dumped = [];
        let offset = 0;
        while (true) {
            const uint = this.mem.sliceNoPad(offset, 32);
            if (!uint.length) {
                return dumped;
            }
            dumped.push(buffer_1.Buffer.from(uint).toString('hex'));
            offset += 33;
        }
    }
    spyOps(fn) {
        const nameLen = Math.max(...exports.ops.map(o => o.name.length));
        for (let i = 0; i < exports.ops.length; i++) {
            const o = exports.ops[i];
            const paddedopname = o.name.padEnd(nameLen, ' ');
            const old = (this[o.name] ?? o).bind(this);
            old.isAsync = o.isAsync;
            this[o.name] = fn(old, o.name, paddedopname, i);
        }
    }
    watch(handler) {
        this.spyOps((fn, opname, padded, opcode) => fn.isAsync
            ? async (...args) => {
                const spy = this.opSpy = [];
                await fn(...args);
                this.opSpy = null;
                handler(opcode, opname, padded, spy, this.knownSequence);
            } : (...args) => {
            const spy = this.opSpy = [];
            fn(...args);
            this.opSpy = null;
            handler(opcode, opname, padded, spy, this.knownSequence);
        });
    }
    onMemChange(fn) {
        this._onMemChange ?? (this._onMemChange = []);
        this._onMemChange?.push(fn);
        this.mem.onChange = () => this.notifyMemChanged = true;
        const notif = () => {
            if (this.notifyMemChanged) {
                let dumped = undefined;
                const cloned = () => dumped ?? (dumped = this.mem.dump());
                this._onMemChange?.forEach(c => c(cloned));
            }
        };
        this.spyOps(o => o.isAsync
            ? async (...args) => {
                this.notifyMemChanged = false;
                await o(...args);
                notif();
            } : (...args) => {
            this.notifyMemChanged = false;
            o(...args);
            notif();
        });
    }
    onStartingCall(fn) {
        this._onStartCall ?? (this._onStartCall = []);
        this._onStartCall.push(fn);
    }
    onEndingCall(fn) {
        this._onEndCall ?? (this._onEndCall = []);
        this._onEndCall.push(fn);
    }
    onLog(fn) {
        this._onLog ?? (this._onLog = []);
        this._onLog.push(fn);
    }
    onResult(handler) {
        this._onResult.push(handler);
    }
    pop() {
        const ret = this.stack.pop();
        if (!ret) {
            throw new Error('stack undeflow');
        }
        this.opSpy?.push((0, utils_1.dumpU256)(ret));
        return ret;
    }
    popAsNum() {
        const poped = this.pop();
        return (0, utils_1.toNumberSafe)(poped);
    }
    popAsBool() {
        const poped = this.pop();
        return !poped.eq(0);
    }
    startKnownSequence(seq) {
        this.knownSequence = seq;
    }
    endKnownSequence() {
        this.knownSequence = null;
    }
    getStack(n) {
        const at = this.stack.length - n;
        if (at < 0) {
            throw new Error('not enough values on stack');
        }
        return this.stack[at].copy();
    }
    push(elt) {
        this.opSpy?.push(`➡ ${(0, utils_1.dumpU256)(elt)}`);
        this.stack.push(elt);
    }
    pushBool(elt) {
        this.opSpy?.push(`➡ ${elt ? '1' : '0'}`);
        this.stack.push(elt ? (0, uint256_1.U256)(1) : (0, uint256_1.U256)(0));
    }
    op_stop() {
        this.state.decrementGas(3);
        this.stop = {
            type: 'stop',
            newState: this.state,
            gas: this.state.gasSpent,
        };
    }
    op_add() {
        this.state.decrementGas(3);
        this.push(this.pop().add(this.pop()));
    }
    op_mul() {
        this.state.decrementGas(3);
        this.push(this.pop().mul(this.pop()));
    }
    op_sub() {
        this.state.decrementGas(3);
        this.push(this.pop().sub(this.pop()));
    }
    op_div() {
        this.state.decrementGas(3);
        this.push(this.pop().div(this.pop()));
    }
    op_sdiv() {
        this.state.decrementGas(3);
        this.push(this.pop().sdiv(this.pop()));
    }
    op_mod() {
        this.state.decrementGas(3);
        this.push(this.pop().mod(this.pop()));
    }
    op_smod() {
        this.state.decrementGas(3);
        this.push(this.pop().smod(this.pop()));
    }
    op_addmod() {
        this.state.decrementGas(3);
        throw new Error('not implemented: addmod');
    }
    op_mulmod() {
        this.state.decrementGas(3);
        this.push(this.pop().mul(this.pop()).mod(this.pop()));
    }
    op_exp() {
        this.state.decrementGas(3);
        const a = this.pop();
        const exp = this.popAsNum();
        this.push(a.pow(exp));
    }
    op_signextend() {
        this.state.decrementGas(3);
        const b = this.popAsNum();
        const x = this.pop();
        const leftMost = (b + 1) * 8 - 1;
        const isNeg = x.testBit(leftMost);
        const copy = x.copy();
        if (isNeg) {
            for (let i = leftMost; i < 256; i++) {
                copy.setBit(i, true);
            }
        }
        else {
            for (let i = leftMost; i < 256; i++) {
                copy.clearBit(i, true);
            }
        }
        this.push(copy);
    }
    op_unused() {
        this.state.decrementGas(3);
        throw new Error('not implemented: unused');
    }
    op_lt() {
        this.state.decrementGas(3);
        this.pushBool(this.pop().lt(this.pop()));
    }
    op_gt() {
        this.state.decrementGas(3);
        this.pushBool(this.pop().gt(this.pop()));
    }
    op_slt() {
        this.state.decrementGas(3);
        this.pushBool(this.pop().slt(this.pop()));
    }
    op_sgt() {
        this.state.decrementGas(3);
        this.pushBool(this.pop().sgt(this.pop()));
    }
    op_eq() {
        this.state.decrementGas(3);
        this.pushBool(this.pop().eq(this.pop()));
    }
    op_iszero() {
        this.state.decrementGas(3);
        this.pushBool(this.pop().eq(ZERO));
    }
    op_and() {
        this.state.decrementGas(3);
        this.push(this.pop().and(this.pop()));
    }
    op_or() {
        this.state.decrementGas(3);
        this.push(this.pop().or(this.pop()));
    }
    op_xor() {
        this.state.decrementGas(3);
        this.push(this.pop().xor(this.pop()));
    }
    op_not() {
        this.state.decrementGas(3);
        this.push(this.pop().not(true));
    }
    op_byte() {
        this.state.decrementGas(3);
        const i = this.popAsNum();
        const x = this.pop().toByteArray();
        this.push((0, uint256_1.U256)(x[i]));
    }
    op_shl() {
        this.state.decrementGas(3);
        const n = this.popAsNum();
        this.push(this.pop().shiftLeft(n));
    }
    op_shr() {
        this.state.decrementGas(3);
        const n = this.popAsNum();
        this.push(this.pop().shiftRight(n));
    }
    op_sar() {
        this.state.decrementGas(3);
        const n = this.popAsNum();
        this.push(this.pop().sar(n));
    }
    op_sha3() {
        this.state.decrementGas(3);
        const toHash = this.getData();
        this.push((0, utils_1.shaOf)(toHash));
    }
    op_address() {
        this.state.decrementGas(3);
        this.push(this.state.address);
    }
    async op_balance() {
        this.state.decrementGas(100);
        const address = this.pop();
        const bal = await this.state.getStorageOf(address).getBalance();
        this.push(bal);
    }
    op_origin() {
        this.state.decrementGas(3);
        this.push(this.state.origin);
    }
    op_caller() {
        this.state.decrementGas(3);
        this.push(this.state.caller);
    }
    op_callvalue() {
        this.state.decrementGas(3);
        this.push(this.state.callvalue);
    }
    op_calldataload() {
        this.state.decrementGas(3);
        const addr = this.popAsNum();
        const data = this.state.calldata.slice(addr, 32);
        this.push((0, utils_1.toUint)(data));
    }
    op_calldatasize() {
        this.state.decrementGas(3);
        this.push((0, uint256_1.U256)(this.state.calldata.size));
    }
    op_calldatacopy() {
        this.state.decrementGas(3);
        this.copyDataToMem(this.state.calldata);
    }
    op_codesize() {
        this.state.decrementGas(3);
        this.push((0, uint256_1.U256)(this.code.code.size));
    }
    op_codecopy() {
        this.state.decrementGas(3);
        this.copyDataToMem(this.code.code);
    }
    copyDataToMem(from) {
        const destOffset = this.popAsNum();
        const offset = this.popAsNum();
        const size = this.popAsNum();
        for (let i = 0; i < size; i++) {
            const byte = from.getByte(offset + i) ?? 0;
            this.mem.set(destOffset + i, byte);
        }
    }
    op_gasprice() {
        this.state.decrementGas(3);
        this.push(this.state.gasPrice);
    }
    async op_extcodesize() {
        const address = this.pop();
        const contract = await this.state.getContract(address);
        this.push((0, uint256_1.U256)(contract.code?.size ?? 0));
    }
    op_extcodecopy() {
        this.state.decrementGas(3);
        throw new Error('not implemented: extcodecopy');
    }
    op_returndatasize() {
        this.state.decrementGas(3);
        this.push((0, uint256_1.U256)(this.lastReturndata.size));
    }
    op_returndatacopy() {
        this.state.decrementGas(3);
        const destOffset = this.popAsNum();
        const offset = this.popAsNum();
        const size = this.popAsNum();
        if (!size) {
            return;
        }
        if (offset >= this.lastReturndata.size) {
            throw new Error(`Cannot execute "returndatacopy" of ${size} bytes at an index (${offset}) that is higher than "returndatasize" (${this.lastReturndata.size})`);
        }
        for (let i = 0; i < size; i++) {
            this.mem.set(destOffset + i, this.lastReturndata.getByte(offset + i));
        }
    }
    op_extcodehash() {
        this.state.decrementGas(3);
        throw new Error('not implemented: extcodehash');
    }
    op_blockhash() {
        this.state.decrementGas(3);
        throw new Error('not implemented: blockhash');
    }
    op_coinbase() {
        this.state.decrementGas(3);
        throw new Error('not implemented: coinbase');
    }
    op_timestamp() {
        this.state.decrementGas(3);
        this.push((0, uint256_1.U256)(this.state.timestamp));
    }
    async op_number() {
        this.state.decrementGas(3);
        let number = (0, utils_1.to32ByteBuffer)(await this.state.session.rpc.getBlock());
        this.push((0, uint256_1.U256)(number.buffer));
    }
    op_difficulty() {
        this.state.decrementGas(3);
        this.push(this.state.difficulty.copy());
    }
    op_gaslimit() {
        this.state.decrementGas(3);
        this.push(this.state.gasLimit);
    }
    async op_chainid() {
        this.state.decrementGas(3);
        let chainId = (0, utils_1.to32ByteBuffer)(await this.state.session.rpc.getChainId());
        this.push((0, uint256_1.U256)(chainId.buffer));
    }
    async op_selfbalance() {
        this.push(await this.state.getBalance());
    }
    op_basefee() {
        this.state.decrementGas(3);
        throw new Error('not implemented: basefee');
    }
    op_pop() {
        this.state.decrementGas(3);
        this.pop();
    }
    op_mload() {
        this.state.decrementGas(3);
        this.push(this.mem.get(this.popAsNum()));
    }
    op_mstore() {
        this.state.decrementGas(3);
        this.mem.setUint256(this.popAsNum(), this.pop());
    }
    op_mstore8() {
        this.state.decrementGas(3);
        const byte = this.pop().shiftRight(0xF0);
        this.mem.setUint256(this.popAsNum(), byte);
    }
    async op_sload() {
        this.push(await this.state.getStorage(this.pop()));
    }
    op_sstore() {
        this.state.decrementGas(3);
        this.state = this.state.setStorage(this.pop(), this.pop());
    }
    op_jump() {
        this.state.decrementGas(3);
    }
    op_jumpi() {
        this.state.decrementGas(3);
    }
    op_pc(num) {
        this.state.decrementGas(3);
        this.push((0, uint256_1.U256)(num));
    }
    op_msize() {
        this.state.decrementGas(3);
        this.push((0, uint256_1.U256)(this.mem.size));
    }
    op_gas() {
        this.state.decrementGas(3);
        this.push(this.state.gas);
    }
    op_jumpdest() {
        this.state.decrementGas(3);
    }
    doOpPush(toPush) {
        this.push((0, utils_1.toUint)(new Uint8Array(toPush)));
    }
    op_push1(num) {
        this.state.decrementGas(3);
        this.push((0, uint256_1.U256)(num));
    }
    op_push2(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push3(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push4(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push5(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push6(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push7(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push8(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push9(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push10(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push11(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push12(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push13(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push14(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push15(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push16(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push17(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push18(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push19(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push20(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push21(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push22(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push23(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push24(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push25(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push26(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push27(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push28(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push29(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push30(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push31(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_push32(data) {
        this.state.decrementGas(3);
        this.doOpPush(data);
    }
    op_dup1() {
        this.state.decrementGas(3);
        this.push(this.getStack(1));
    }
    op_dup2() {
        this.state.decrementGas(3);
        this.push(this.getStack(2));
    }
    op_dup3() {
        this.state.decrementGas(3);
        this.push(this.getStack(3));
    }
    op_dup4() {
        this.state.decrementGas(3);
        this.push(this.getStack(4));
    }
    op_dup5() {
        this.state.decrementGas(3);
        this.push(this.getStack(5));
    }
    op_dup6() {
        this.state.decrementGas(3);
        this.push(this.getStack(6));
    }
    op_dup7() {
        this.state.decrementGas(3);
        this.push(this.getStack(7));
    }
    op_dup8() {
        this.state.decrementGas(3);
        this.push(this.getStack(8));
    }
    op_dup9() {
        this.state.decrementGas(3);
        this.push(this.getStack(9));
    }
    op_dup10() {
        this.state.decrementGas(3);
        this.push(this.getStack(10));
    }
    op_dup11() {
        this.state.decrementGas(3);
        this.push(this.getStack(11));
    }
    op_dup12() {
        this.state.decrementGas(3);
        this.push(this.getStack(12));
    }
    op_dup13() {
        this.state.decrementGas(3);
        this.push(this.getStack(13));
    }
    op_dup14() {
        this.state.decrementGas(3);
        this.push(this.getStack(14));
    }
    op_dup15() {
        this.state.decrementGas(3);
        this.push(this.getStack(15));
    }
    op_dup16() {
        this.state.decrementGas(3);
        this.push(this.getStack(16));
    }
    doSwap(n) {
        const target = this.stack.length - n - 1;
        if (target < 0) {
            throw new Error('not enough values on stack');
        }
        const latest = this.stack.pop();
        this.stack.push(this.stack[target]);
        this.stack[target] = latest;
    }
    op_swap1() {
        this.state.decrementGas(3);
        this.doSwap(1);
    }
    op_swap2() {
        this.state.decrementGas(3);
        this.doSwap(2);
    }
    op_swap3() {
        this.state.decrementGas(3);
        this.doSwap(3);
    }
    op_swap4() {
        this.state.decrementGas(3);
        this.doSwap(4);
    }
    op_swap5() {
        this.state.decrementGas(3);
        this.doSwap(5);
    }
    op_swap6() {
        this.state.decrementGas(3);
        this.doSwap(6);
    }
    op_swap7() {
        this.state.decrementGas(3);
        this.doSwap(7);
    }
    op_swap8() {
        this.state.decrementGas(3);
        this.doSwap(8);
    }
    op_swap9() {
        this.state.decrementGas(3);
        this.doSwap(9);
    }
    op_swap10() {
        this.state.decrementGas(3);
        this.doSwap(10);
    }
    op_swap11() {
        this.state.decrementGas(3);
        this.doSwap(11);
    }
    op_swap12() {
        this.state.decrementGas(3);
        this.doSwap(12);
    }
    op_swap13() {
        this.state.decrementGas(3);
        this.doSwap(13);
    }
    op_swap14() {
        this.state.decrementGas(3);
        this.doSwap(14);
    }
    op_swap15() {
        this.state.decrementGas(3);
        this.doSwap(15);
    }
    op_swap16() {
        this.state.decrementGas(3);
        this.doSwap(16);
    }
    getData() {
        const offset = this.popAsNum();
        const size = this.popAsNum();
        return this.mem.slice(offset, size);
    }
    op_log0() {
        this.state.decrementGas(3);
        const log = { address: this.state.address, data: this.getData(), topics: [], };
        this.logs.push(log);
        this._onLog?.forEach(fn => fn(log));
    }
    op_log1() {
        this.state.decrementGas(3);
        const log = { address: this.state.address, data: this.getData(), topics: [this.pop()], };
        this.logs.push(log);
        this._onLog?.forEach(fn => fn(log));
    }
    op_log2() {
        this.state.decrementGas(3);
        const log = { address: this.state.address, data: this.getData(), topics: [this.pop(), this.pop()], };
        this.logs.push(log);
        this._onLog?.forEach(fn => fn(log));
    }
    op_log3() {
        this.state.decrementGas(3);
        const log = { address: this.state.address, data: this.getData(), topics: [this.pop(), this.pop(), this.pop()], };
        this.logs.push(log);
        this._onLog?.forEach(fn => fn(log));
    }
    op_log4() {
        this.state.decrementGas(3);
        const log = { address: this.state.address, data: this.getData(), topics: [this.pop(), this.pop(), this.pop(), this.pop()], };
        this.logs.push(log);
        this._onLog?.forEach(fn => fn(log));
    }
    op_create() {
        this.state.decrementGas(3);
        throw new Error('not implemented: create');
    }
    async op_call() {
        const gas = this.pop();
        const contract = this.pop();
        const value = this.pop();
        const argsOffset = this.popAsNum();
        const argsSize = this.popAsNum();
        const retOffset = this.popAsNum();
        const retSize = this.popAsNum();
        const code = await this.state.getContract(contract);
        const calldata = this.mem.slice(argsOffset, argsSize);
        const newState = await this.state
            .pushCallTo(contract, value, calldata, retSize, gas);
        const executor = new Executor(newState, code);
        this._onStartCall?.forEach(c => c(executor, 'call'));
        const result = await executor.execute();
        this.setCallResult(result, retOffset, retSize, executor.logs, 'call');
    }
    setCallResult(result, retOffset, retSize, logs, type) {
        const success = (0, interfaces_1.isSuccess)(result);
        if (type !== 'create2') {
            this.pushBool(success);
        }
        this._onEndCall?.forEach(c => c(this, type, success, result));
        if (type !== 'create2') {
            this.lastReturndata = new mem_reader_1.MemReader([]);
        }
        if ((0, interfaces_1.isFailure)(result)) {
            this.state.decrementGas(result.gas);
            return;
        }
        this.state = result.newState
            .popCallStack();
        this.logs.push(...logs);
        this.state.decrementGas(result.gas);
        if (!result.data?.length) {
            return;
        }
        if (type !== 'create2') {
            this.lastReturndata = new mem_reader_1.MemReader([...result.data]);
        }
        for (let i = 0; i < retSize; i++) {
            this.mem.set(retOffset + i, result.data[i] ?? 0);
        }
    }
    op_callcode() {
        this.state.decrementGas(3);
        throw new Error('not implemented: callcode');
    }
    op_return() {
        this.state.decrementGas(3);
        this.stop = {
            type: 'return',
            data: this.getData(),
            gas: this.state.gasSpent,
            newState: this.state,
        };
    }
    async op_delegatecall() {
        const gas = this.pop();
        const contract = this.pop();
        const argsOffset = this.popAsNum();
        const argsSize = this.popAsNum();
        const retOffset = this.popAsNum();
        const retSize = this.popAsNum();
        const code = await this.state.getContract(contract);
        const calldata = this.mem.slice(argsOffset, argsSize);
        const newState = this.state
            .pushDelegatecallTo(contract, calldata, retSize, gas);
        const executor = new Executor(newState, code);
        this._onStartCall?.forEach(c => c(executor, 'delegatecall'));
        const result = await executor.execute();
        this.setCallResult(result, retOffset, retSize, executor.logs, 'delegatecall');
    }
    async op_create2() {
        this.state.decrementGas(3);
        const value = (0, uint256_1.U256)(this.popAsNum());
        const offset = this.popAsNum();
        const size = this.popAsNum();
        const salt = this.pop();
        const code = this.mem.slice(offset, size);
        const accountAddress = await this.doCreate2(salt, code, value);
        this.push(accountAddress);
    }
    async doCreate2(salt, code, value) {
        const accountAddress = this.computeCreate2Address(salt, code);
        const newState = await this.state
            .pushCallTo(accountAddress, value, code, 0x20, this.state.gas);
        const compiledDeployer = (0, compiler_1.compileCode)(code, undefined, accountAddress, undefined, undefined);
        const executor = new Executor(newState, compiledDeployer);
        this._onStartCall?.forEach(c => c(executor, 'create2'));
        const result = await executor.execute();
        this.setCallResult(result, 0, 0, executor.logs, 'create2');
        if (!result.data) {
            throw new Error('no data returned from deployer');
        }
        const compiledContract = (0, compiler_1.compileCode)(result.data, undefined, accountAddress, undefined, undefined);
        this.state = this.state.setContract(compiledContract);
        return accountAddress;
    }
    computeCreate2Address(salt, code) {
        const stringSender = this.state.address.toString(16).padStart(40, "0");
        const stringSalt = salt.toString(16).padStart(64, "0");
        const stringCode = buffer_1.Buffer.from(code).toString('hex');
        const bytesCode = (0, utils_1.parseBuffer)(stringCode);
        const hashedCode = (0, keccak256_1.default)(bytesCode);
        const hexStringToHash = "ff" + stringSender + stringSalt + hashedCode.toString('hex');
        const bytesToHash = (0, utils_1.parseBuffer)(hexStringToHash);
        const hash = (0, keccak256_1.default)(bytesToHash);
        const address = "0x" + hash.toString('hex').slice(-40);
        return (0, uint256_1.U256)(address);
    }
    async op_staticcall() {
        const gas = this.pop();
        const contract = this.pop();
        const argsOffset = this.popAsNum();
        const argsSize = this.popAsNum();
        const retOffset = this.popAsNum();
        const retSize = this.popAsNum();
        const code = await this.state.getContract(contract);
        const calldata = this.mem.slice(argsOffset, argsSize);
        const newState = this.state
            .pushStaticcallTo(contract, calldata, retSize, gas);
        const executor = new Executor(newState, code);
        this._onStartCall?.forEach(c => c(executor, 'staticcall'));
        const result = await executor.execute();
        this.setCallResult(result, retOffset, retSize, executor.logs, 'staticcall');
    }
    op_revert() {
        this.state.decrementGas(3);
        this.stop = {
            type: 'revert',
            data: this.getData(),
            gas: this.state.gasSpent,
        };
    }
    op_invalid() {
        this.state.decrementGas(3);
        throw new Error('not implemented: invalid');
    }
    op_selfdestruct() {
        this.state.decrementGas(3);
        throw new Error('not implemented: selfdestruct');
    }
}
__decorate([
    asyncOp(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], Executor.prototype, "op_balance", null);
__decorate([
    asyncOp(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], Executor.prototype, "op_extcodesize", null);
__decorate([
    asyncOp(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], Executor.prototype, "op_number", null);
__decorate([
    asyncOp(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], Executor.prototype, "op_chainid", null);
__decorate([
    asyncOp(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], Executor.prototype, "op_selfbalance", null);
__decorate([
    asyncOp(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], Executor.prototype, "op_sload", null);
__decorate([
    asyncOp(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], Executor.prototype, "op_call", null);
__decorate([
    asyncOp(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], Executor.prototype, "op_delegatecall", null);
__decorate([
    asyncOp(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], Executor.prototype, "op_create2", null);
__decorate([
    asyncOp(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], Executor.prototype, "op_staticcall", null);
exports.Executor = Executor;
const p = Executor.prototype;
exports.ops = [p.op_stop, p.op_add, p.op_mul, p.op_sub, p.op_div, p.op_sdiv, p.op_mod, p.op_smod, p.op_addmod, p.op_mulmod, p.op_exp, p.op_signextend, p.op_unused, p.op_unused, p.op_unused, p.op_unused,
    p.op_lt, p.op_gt, p.op_slt, p.op_sgt, p.op_eq, p.op_iszero, p.op_and, p.op_or, p.op_xor, p.op_not, p.op_byte, p.op_shl, p.op_shr, p.op_sar, p.op_unused, p.op_unused,
    p.op_sha3, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused,
    p.op_address, p.op_balance, p.op_origin, p.op_caller, p.op_callvalue, p.op_calldataload, p.op_calldatasize, p.op_calldatacopy, p.op_codesize, p.op_codecopy, p.op_gasprice, p.op_extcodesize, p.op_extcodecopy, p.op_returndatasize, p.op_returndatacopy, p.op_extcodehash,
    p.op_blockhash, p.op_coinbase, p.op_timestamp, p.op_number, p.op_difficulty, p.op_gaslimit, p.op_chainid, p.op_selfbalance, p.op_basefee, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused,
    p.op_pop, p.op_mload, p.op_mstore, p.op_mstore8, p.op_sload, p.op_sstore, p.op_jump, p.op_jumpi, p.op_pc, p.op_msize, p.op_gas, p.op_jumpdest, p.op_unused, p.op_unused, p.op_unused, p.op_unused,
    p.op_push1, p.op_push2, p.op_push3, p.op_push4, p.op_push5, p.op_push6, p.op_push7, p.op_push8, p.op_push9, p.op_push10, p.op_push11, p.op_push12, p.op_push13, p.op_push14, p.op_push15, p.op_push16,
    p.op_push17, p.op_push18, p.op_push19, p.op_push20, p.op_push21, p.op_push22, p.op_push23, p.op_push24, p.op_push25, p.op_push26, p.op_push27, p.op_push28, p.op_push29, p.op_push30, p.op_push31, p.op_push32,
    p.op_dup1, p.op_dup2, p.op_dup3, p.op_dup4, p.op_dup5, p.op_dup6, p.op_dup7, p.op_dup8, p.op_dup9, p.op_dup10, p.op_dup11, p.op_dup12, p.op_dup13, p.op_dup14, p.op_dup15, p.op_dup16,
    p.op_swap1, p.op_swap2, p.op_swap3, p.op_swap4, p.op_swap5, p.op_swap6, p.op_swap7, p.op_swap8, p.op_swap9, p.op_swap10, p.op_swap11, p.op_swap12, p.op_swap13, p.op_swap14, p.op_swap15, p.op_swap16,
    p.op_log0, p.op_log1, p.op_log2, p.op_log3, p.op_log4, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused,
    p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused,
    p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused,
    p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused,
    p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_unused,
    p.op_create, p.op_call, p.op_callcode, p.op_return, p.op_delegatecall, p.op_create2, p.op_unused, p.op_unused, p.op_unused, p.op_unused, p.op_staticcall, p.op_unused, p.op_unused, p.op_revert, p.op_invalid, p.op_selfdestruct
];
//# sourceMappingURL=executor.js.map